import {configConstants, getConfigItem} from '@servicenow/ui-config';

// Persistent database key/value cache implemented over IndexedDB with an async interface

const {log, error} = console;
const {GQL_CACHE_CAPACITY} = configConstants;

const cacheCapacity = getConfigItem(GQL_CACHE_CAPACITY);

export const openStore = async params => {
	const {dbName, storeName, schemaVersion, options = {}} = params;

	let db = undefined;
	const cachePolicy = {
		capacity: cacheCapacity,
		onInit: () => [],
		onHit: () => ({keysToDelete: [], state: []}),
		onSet: () => ({keysToDelete: [], state: []}),
		onDelete: () => [],
		onClear: () => undefined,
		...options.cachePolicy // Caller-provided options override the defaults above
	};

	const getStore = mode => db.transaction(storeName, mode).objectStore(storeName);

	const self = {
		async get(key, defaultValue = undefined) {
			return new Promise(resolve => {
				const store = getStore('readwrite');
				const getRequest = store.get(key);

				getRequest.onerror = err => {
					error(
						`Error retrieving item in getter for '${key}': Error code: ${err.target.errorCode}`
					);
					resolve(defaultValue);
				};

				getRequest.onsuccess = event => {
					// log(`Item for ${key} is ${event.target.result}`);
					const entry = event.target.result;

					// Create a set request and save the metadata for this key/value (don't resolve this request until the set is complete)
					const {keysToDelete} = cachePolicy.onHit(key);

					// TODO: Delete all keys the cachePolicy asked us to
					for (const key of keysToDelete) store.delete(key);
					// TODO: Probably don't really need to wait for these to delete, so let's not for now

					self
						.set(key, entry.value)
						.then(() => resolve(entry.value))
						.catch(() => resolve(defaultValue));
				};
			});
		},

		async getAll(includeMetaData = false) {
			return new Promise(resolve => {
				// log(`getAll(): dbName=${dbName}, storeName=${storeName}`);
				const store = getStore('readonly');
				const getAllKeysRequest = store.getAllKeys();
				const all = {};

				getAllKeysRequest.onerror = err => {
					error(`Error in getAll(): Error code: ${err.target.errorCode}`);
					resolve({});
				};

				getAllKeysRequest.onsuccess = event => {
					// log(`Result for getAll() is ${JSON.stringify(event.target.result)}`);

					// Get all the entries
					const keys = event.target.result;
					let numRemaining = keys.length;
					if (numRemaining === 0) resolve({});

					for (const key of keys) {
						const getRequest = store.get(key);

						getRequest.onerror = err => {
							error(`Error in getAll(): Error code: ${err.target.errorCode}`);
							resolve({});
						};

						getRequest.onsuccess = event => {
							const entry = event.target.result;
							all[key] = includeMetaData ? entry : entry.value;
							numRemaining -= 1;
							if (numRemaining === 0) resolve(all);
						};
					}
				};
			});
		},

		async set(key, value) {
			return new Promise((resolve, reject) => {
				const store = getStore('readwrite');

				const {keysToDelete, state} = cachePolicy.onSet(key);

				const putRequest = store.put({key, value, cachePolicyState: state}, key);

				// TODO: Delete all keys the cachePolicy asked us to
				for (const key of keysToDelete) store.delete(key);
				// TODO: Probably don't really need to wait for these to delete, so let's not for now

				putRequest.onerror = err => {
					error(`Error setting item in set() for '${key}': Error code: ${err.target.errorCode}`);
					reject(err.target.errorCode);
				};

				putRequest.onsuccess = event => {
					// log(`Item for ${key} is ${event.target.result}`);
					resolve(event.target.value);
				};
			});
		},

		async setMultiple(obj) {
			return new Promise((resolve, reject) => {
				const store = getStore('readwrite');

				let numRequestsMade = 0;
				let numRequestsComplete = 0;

				for (const [key, value] of obj) {
					const {keysToDelete, state} = cachePolicy.onSet(key);

					// TODO: Delete all keys the cachePolicy asked us to
					for (const key of keysToDelete) store.delete(key);
					// TODO: Probably don't really need to wait for these to delete, so let's not for now

					const putRequest = store.put({key, value, cachePolicyState: state}, key);

					// TODO: Should abort() if any failures and ideally abort the txn
					putRequest.onerror = err => {
						error(`Error setting item in setMultiple: Error code: ${err.target.errorCode}`);
						numRequestsComplete += 1;
						if (numRequestsComplete === numRequestsMade) reject(err.target.errorCode);
					};

					putRequest.onsuccess = () => {
						numRequestsComplete += 1;
						if (numRequestsComplete === numRequestsMade) resolve();
					};

					numRequestsMade += 1;
				}
			});
		},

		async has(key) {
			return new Promise((resolve, reject) => {
				const store = getStore('readonly');
				const countRequest = store.count(key);

				countRequest.onerror = err => {
					error(`Error getting count for '${key}': Error code: ${err.target.errorCode}`);
					reject(err.target.errorCode);
				};

				countRequest.onsuccess = () => {
					resolve(countRequest.result > 0);
				};
			});
		},

		async clear() {
			return new Promise((resolve, reject) => {
				const store = getStore('readwrite');
				const clearRequest = store.clear();

				clearRequest.onerror = err => {
					error(`IndexedDB clear failed.  Error code: ${err.target.errorCode}`);
					reject(err.target.errorCode);
				};

				clearRequest.onsuccess = () => {
					// options.onClear();
					resolve();
					// log('Store Cleared');
				};
			});
		},

		async delete(key) {
			return new Promise((resolve, reject) => {
				const store = getStore('readwrite');
				const deleteRequest = store.delete(key);

				deleteRequest.onerror = err => {
					error(`IndexedDB clear failed.  Error code: ${err.target.errorCode}`);
					reject(err.target.errorCode);
				};

				deleteRequest.onsuccess = () => {
					resolve();
				};
			});
		},

		async size() {
			return new Promise((resolve, reject) => {
				const store = getStore('readonly');
				const countRequest = store.count();

				countRequest.onerror = err => {
					error(
						`Error getting total record count for ${storeName}: Error code: ${err.target.errorCode}`
					);
					reject(err.target.errorCode);
				};

				countRequest.onsuccess = () => {
					resolve(countRequest.result);
				};
			});
		},

		close() {
			db.close();
		}
	};

	// Open the database and return an object that can reference the db via closure
	return new Promise((resolve, reject) => {
		if (!globalThis.indexedDB) {
			reject(
				'Browser does not seem to support IndexedDB, which is required for this feature to work.'
			);
			return;
		}

		const dbRequest = globalThis.indexedDB.open(dbName, schemaVersion);

		dbRequest.onsuccess = event => {
			db = event.target.result;
			// log('openStore.onsuccess');

			// Give the policy a chance to update its internal state
			self.getAll(true).then(allEntries => {
				cachePolicy.onInit(allEntries);
				resolve(self);
			});
		};

		dbRequest.onerror = err => {
			log(`err=${dbRequest.error}`);
			error(
				`openStore: IndexedDB error: ${err.target.errorCode}.\nBrowser possibly does not support IndexedDB.`
			);
			reject(err.target.error);
		};

		dbRequest.onupgradeneeded = event => {
			// log('openStore.onupgradeneeded');

			db = event.target.result;

			try {
				db.createObjectStore(storeName, {
					autoIncrement: true
				});
			} catch (e) {
				error(`EXCEPTION: ${e}`);
			}
		};
	});
};

async function dropDatabaseImpl(dbName) {
	return new Promise((resolve, reject) => {
		if (!globalThis.indexedDB) {
			reject(
				'Browser does not seem to support IndexedDB, which is required for this feature to work.'
			);
			return;
		}

		var deleteRequest = globalThis.indexedDB.deleteDatabase(dbName);

		deleteRequest.onsuccess = () => {
			// log('Deleted database successfully');
			resolve();
		};

		deleteRequest.onerror = err => {
			error(`Error deleting database with name: ${dbName}  Error code: ${deleteRequest.error}`);
			reject(err.target.errorCode);
		};

		deleteRequest.onblocked = () => {
			// log('Database is busy.  Please try again later.  Goodbye.');
			reject('Database is busy.');
		};
	});
}

// Drop an entire database
export async function dropDatabase(dbName) {
	let result;
	// This needs to run at least twice if the database was recently closed
	do {
		try {
			result = await dropDatabaseImpl(dbName);
		} catch (e) {
			// Database was busy and could not be deleted
		}
	} while (result);
}

// // Drop a single, named store within a given database
// async function dropStoreImpl(storeName, dbName, version) {
// 	log('dropStore 1');
// 	// Open the database and return an object that can reference the db via closure
// 	return new Promise((resolve, reject) => {
// 		if (!globalThis.indexedDB) {
// 			reject(
// 				'Browser does not seem to support IndexedDB, which is required for this feature to work.'
// 			);
// 			return;
// 		}

// 		log('dropStore 2');
// 		const dbRequest = globalThis.indexedDB.open(dbName, version);
// 		log('dropStore 3');

// 		dbRequest.onsuccess = () => {
// 			log('opened!');
// 			resolve();
// 		};

// 		dbRequest.onerror = err => {
// 			log(`err=${JSON.stringify(err)}`);
// 			error(`dropStore: IndexedDB error: ${err.target.errorCode}`);
// 			reject(err.target.error);
// 		};

// 		log('dropStore 4');

// 		dbRequest.onupgradeneeded = event => {
// 			log('dbRequest.onupgradeneeded');
// 			const db = event.target.result;
// 			log(`db=${db} storeName=${storeName}`);

// 			db.deleteObjectStore(storeName);
// 			resolve();
// 		};

// 		log('dropStore 6');
// 	});
// }

// export async function dropStore(storeName, dbName, version) {
// 	try {
// 		await dropStoreImpl(storeName, dbName, version);
// 	} catch (e) {
// 		log(`dropStore Exception: ${e}`);
// 	}
// }
